import axios from 'axios'
import {
  MECConfirmationVeriage,
  MECDataByApiPayload,
  UpdateMECValuePayload,
} from '../types/mec.types'
import axiosInstance from '../utils/axiosInstance'
import { API_END_POINT } from '../utils/environment'
import { GetGenericEntityListingApiType } from '../types/entity.types'
import { resolveEntityName } from '../utils/entities.utils'
import { validateApiParams } from '../utils/functions'

const createHCPConsentPortal = async (programId: string) => {
  return await axiosInstance.post(
    `${API_END_POINT.CONSENT_REQUEST}consent-portals/programs/${programId}`
  )
}
const getMessageConfigurationData = async (portalId: string) => {
  return await axiosInstance.get(
    `${API_END_POINT.CONSENT_REQUEST}consent-portals/${portalId}/confirmation-message`
  )
}
const getUnathenticatedMessageConfigurationData = async (portalId: string) => {
  return await axiosInstance.get(
    `${API_END_POINT.CONSENT_REQUEST}public/consent-portals/${portalId}/confirmation-message`
  )
}
const getConsentPortalsData = async (
  sortInfo?: {
    sortKey?: string
    sortDirection?: string
  },
  size?: number,
  page?: number
) => {
  const params: { size?: number; page?: number; sort?: string } = {
    size,
    page,
  }

  if (
    sortInfo?.sortKey &&
    sortInfo?.sortDirection &&
    sortInfo.sortDirection !== 'disabled'
  ) {
    params.sort = `${sortInfo.sortKey},${sortInfo.sortDirection}`
  }

  return await axiosInstance.get(
    `${API_END_POINT.CONSENT_REQUEST}consent-portals/all`,
    { params }
  )
}
const putMessageConfigurationData = async (payload: MECConfirmationVeriage) => {
  return await axiosInstance.put(
    `${API_END_POINT.CONSENT_REQUEST}consent-portals/confirmation-message`,
    payload
  )
}
const updateNewConsentPresignedURLImage = async (
  _portalId: number,
  key: string,
  extension: string
) => {
  return await axiosInstance.post(
    `${API_END_POINT.CONSENT_REQUEST}consent-portals/file/${key}`,
    undefined,
    { params: { extension } }
  )
}
const getMECBrandingData = async (payload: MECDataByApiPayload) => {
  return await axiosInstance.post(
    `${API_END_POINT.V3_ENTITY_MANAGER}entity-data/get/hcp_consent_branding`,
    payload
  )
}

const updateMECBrandingData = async (
  payload: (UpdateMECValuePayload | null)[]
) => {
  return await axiosInstance.put(
    `${API_END_POINT.CONSENT_REQUEST}consent-portals/customization-data`,
    payload
  )
}
const getMECNewPresignedURLImage = async (
  fileName: string,
  _portalId: string
) => {
  return await axiosInstance.get(
    `${API_END_POINT.CONSENT_REQUEST}public/consent-portals/file?fileName=${fileName}`
  )
}
const uploadDocumentMappingPdf = async (fileName: string, portalId: string) => {
  return await axiosInstance.post(
    `${API_END_POINT.CONSENT_REQUEST}document-consents/draft-pdf?consentPortalId=${portalId}&documentName=${fileName}`
  )
}
const getMECPortalBrandingDataUnathenticate = async (portalId: string) => {
  return await axiosInstance.get(
    `${API_END_POINT.CONSENT_REQUEST}public/consent-portals/${portalId}/customizationData`
  )
}
const getMECDropdownStates = async () => {
  return await axios.get(`${API_END_POINT.CORE}public/states`)
}
const getMECFieldValidationData = async (
  _portalId: string,
  _consentRequestId: string,
  token: string
) => {
  return await axios.get(
    `${API_END_POINT.CONSENT_REQUEST}public/consent-portals/login-fields?token=${token}`
  )
}

export const getPortalDetailsUnauthenticatedApi = async (token: string) => {
  return await axios.get(
    `${API_END_POINT.CONSENT_REQUEST}public/consent-portals/consent-request?token=${token}`
  )
}
export const downloadSelectedDocuments = async (
  _portalId: string,
  _selectedDocumentIds: string
) => {
  return await axiosInstance.get(
    `${API_END_POINT.CONSENT_REQUEST}public/consent-portals/consent-document/download`
  )
}
export const validateFieldsInMEC = async (
  payload: {
    entity_name: string
    field_value: {
      entity_column: string
      value: string | null
    }[]
  },
  _portalId: string,
  _consentRequestId: string,
  token: string
) => {
  return await axiosInstance.post(
    `${API_END_POINT.CONSENT_REQUEST}public/consent-portals/authenticate?token=${token}`,
    payload?.field_value
  )
}

export const getDocumentAndConsents = async (
  _consentPortalId: string,
  _consentRequestId: string
) => {
  return await axiosInstance.get(
    `${API_END_POINT.CONSENT_REQUEST}public/consent-portals/consent-to-sign`
  )
}

const getMECProgramData = async () => {
  return await axiosInstance.get(`${API_END_POINT.CORE}programs/minimal`)
}
const getMECProductData = async (programId: string) => {
  return await axiosInstance.get(
    `${API_END_POINT.CORE}programs/${programId}/products`
  )
}
const getMECConsentsDropdownData = async (
  portalId: string,
  productId: string,
  consentPersonaId: string
) => {
  return await axiosInstance.get(
    `${API_END_POINT.CONSENT_REQUEST}mec/portal/consents/${portalId}/${productId}/${consentPersonaId}`
  )
}
const getMECRecipientEntityTypeData = async (programId: string) => {
  return await axiosInstance.get(
    `${API_END_POINT.CONSENT_REQUEST}mec/portal/${programId}`
  )
}
export const getEconsentButtonStatus = async (programId: string) => {
  return await axiosInstance.get(
    `${API_END_POINT.CONSENT_REQUEST}mec/portal/beacon/${programId}`
  )
}
const getMECPersonaDetails = async (payload: {
  requestor_key: string | null
  requested_persona: string | null
  requested_persona_key: string | null
}) => {
  return await axiosInstance.post(
    `${API_END_POINT.CONSENT_REQUEST}mec/portal/persona-details`,
    payload
  )
}

const resendMECConsent = async (consentRequestId: string) => {
  return await axiosInstance.put(
    `${API_END_POINT.CONSENT_REQUEST}mec/portal/resend-consent/${consentRequestId}`
  )
}
const revokeMECConsent = async (consentRequestId: string) => {
  return await axiosInstance.put(
    `${API_END_POINT.CONSENT_REQUEST}mec/portal/revoke-consent/${consentRequestId}`
  )
}

const generateMECConsent = async (payload: {
  consent_portal_id: number
  program_id: number
  product_id: number
  consent_persona_id: number
  request_channel_id: number
  email: string
  case_id: number | null
  persona_details: {
    patient_id: number
    requestor_id: number | null
  }
  consents: number[]
}) => {
  return await axiosInstance.post(
    `${API_END_POINT.CONSENT_REQUEST}mec/portal/request/generate`,
    payload
  )
}

export const getProviderDropdownOptions = async (
  payload: GetGenericEntityListingApiType,
  recordId?: string | number
) => {
  const resolvedEntityName = resolveEntityName(
    payload.entity_name,
    payload.entity_id
  )
  const url = recordId
    ? `${API_END_POINT.ENTITY_MANAGER_V3}entity-data/get/${resolvedEntityName}/${recordId}`
    : `${API_END_POINT.ENTITY_MANAGER_V3}entity-data/get/${resolvedEntityName}`
  validateApiParams(
    { entity_name: resolvedEntityName },
    { endpoint: url, method: 'POST' }
  )
  try {
    const requestPayload = {
      ...payload,
      entity_name: resolvedEntityName,
    }
    const res = await axiosInstance.post(url, requestPayload)
    if (res) return res.data
  } catch (err) {
    console.error(err)
  }
  return []
}

export {
  createHCPConsentPortal,
  getMessageConfigurationData,
  putMessageConfigurationData,
  getConsentPortalsData,
  updateNewConsentPresignedURLImage,
  getMECBrandingData,
  updateMECBrandingData,
  getMECNewPresignedURLImage,
  uploadDocumentMappingPdf,
  getMECPortalBrandingDataUnathenticate,
  getMECDropdownStates,
  getMECFieldValidationData,
  getUnathenticatedMessageConfigurationData,
  getMECProgramData,
  getMECProductData,
  getMECRecipientEntityTypeData,
  getMECPersonaDetails,
  getMECConsentsDropdownData,
  resendMECConsent,
  revokeMECConsent,
  generateMECConsent,
}
